import React, { useEffect, useState } from "react";
import { withGlobal } from "utils/globalState";
import { parse } from "query-string";

import { Container } from "@sainsburys-tech/luna-grid";
import { trackEvent } from "@jsainsburyplc/id-ui-common";
import missionConfig from "missionConfig";

import {
  INVALID_CREDENTIALS,
  ACCOUNT_LOCKED,
  INVALID_CAPTCHA,
  PASSWORD_EXPIRED,
  ACCOUNT_INACTIVE,
  DENIED_IOVATION_RESPONSE,
  WEBAUTHN_ASSERTION_FAILED,
} from "constants/codes";
import { DATA_ERROR, CHALLENGE } from "constants/login";
import {
  MFA_CODE_SENT,
  mfaSessionExpiredLoginMessage,
} from "constants/mfaAndOtp";
import {
  hasMfaSessionExpiredNotice,
  clearMfaSessionExpiredNotice,
} from "utils/mfaSession";
import { categoryTypes, actionTypes } from "constants/tracking";
import useCookies from "hooks/useCookies";
import { isIovationEnabled } from "utils/env";

import ActivateAccount from "components/Login/Notifications/ActivateAccount";
import PasswordExpiredNotification from "components/Login/Notifications/PasswordExpiredNotification";
import AccountLockedNotification from "components/Login/Notifications/AccountLockedNotification";
import AccountBlockedNotification from "components/Login/Notifications/AccountBlockedNotification";
import AccountBlockedIovation from "components/AccountBlockedIovation";
import LoginForm from "components/Login/LoginForm";
import {
  SilentLogin,
  PENDING_REG_LOGIN_KEY,
} from "components/Registration/RegistrationForm/SilentLogin";
import { WEBAUTHN_ASSERTION_ERROR } from "constants/messages";
import useDigitalDataTracking from "hooks/useDigitalTrack";

const isIovationEnabledEnv = isIovationEnabled();

const LoginPage = ({ missionName, missionTitle, missionNumber }) => {
  const rootElem = document.querySelector("#root");

  // Legacy mechanism: id-account-auth sets data-error on the #root element when
  // rendering the embedded Thymeleaf template.
  // New mechanism: id-account-auth redirects back to the SPA with ?error_code=...
  // (used by the standalone nginx deployment — no Thymeleaf HTML is rendered).
  const ERROR_CODE =
    parseInt(rootElem.getAttribute(DATA_ERROR)) ||
    parseInt(parse(window.location.search)["error_code"]) ||
    0;

  const { email, loginChallenge: loginChallengeFromCookie } = useCookies();

  // Prefer the challenge from the URL query string — it is always present when
  // Hydra redirects the browser here via URLS_LOGIN and is authoritative for
  // this page load.  The cookie copy may be stale (set by a previous flow)
  // because React's useState (inside useCookies) is initialised once at mount
  // time and does not re-read when Routes.js updates the cookie in a useEffect.
  const loginChallengeFromUrl = parse(window.location.search)[CHALLENGE];
  const loginChallenge = loginChallengeFromUrl || loginChallengeFromCookie;

  // ── Pending registration silent login ────────────────────────────────────────
  // When a user starts registration from a direct link (no login_challenge),
  // SilentLogin saves the credentials in sessionStorage and calls initiateOAuth2Flow.
  // Hydra redirects back here with a fresh login_challenge in the URL.
  // We detect that situation and auto-submit via SilentLogin.
  //
  // We only act when there is a FRESH challenge in the URL (loginChallengeFromUrl).
  // If the user navigated here manually without a challenge, the pending key is
  // ignored to avoid auto-submitting stale credentials.
  const pendingRegLoginRaw = sessionStorage.getItem(PENDING_REG_LOGIN_KEY);
  let pendingRegLogin = null;
  if (pendingRegLoginRaw && loginChallengeFromUrl) {
    try {
      pendingRegLogin = JSON.parse(pendingRegLoginRaw);
    } catch {
      pendingRegLogin = null;
    }
  }

  // ── Hooks must always be called unconditionally (Rules of Hooks) ─────────────
  useDigitalDataTracking(
    "web:groceries:authentication:login",
    "authentication",
    "authLogin",
    "view"
  );

  useEffect(() => {
    sessionStorage.removeItem(MFA_CODE_SENT);
    return () => {
      if (rootElem.getAttribute(DATA_ERROR)) {
        rootElem.removeAttribute(DATA_ERROR);
      }
    };
  }, [rootElem]);

  // Shown once after OtpPage sent the customer back here because the
  // MFA-TOKEN cookie was missing; cleared so a refresh does not show it again.
  const [showMfaSessionExpired] = useState(hasMfaSessionExpiredNotice);
  useEffect(() => {
    clearMfaSessionExpiredNotice();
  }, []);

  useEffect(() => {
    if (ERROR_CODE) {
      trackEvent({
        category: categoryTypes.LOGIN,
        action: actionTypes.ERROR,
        label: `Error code: ${ERROR_CODE}`,
      });
    }
  }, [ERROR_CODE]);

  // ── Conditional renders (after all hooks) ────────────────────────────────────

  // Auto-login after the OAuth2 round-trip that SilentLogin triggered to obtain
  // a fresh challenge for a direct-registration-link flow.
  if (pendingRegLogin) {
    // Clear before rendering so a subsequent page load does not re-trigger.
    sessionStorage.removeItem(PENDING_REG_LOGIN_KEY);
    return (
      <SilentLogin
        username={pendingRegLogin.username}
        password={pendingRegLogin.password}
        isFromRegistration={true}
      />
    );
  }

  if (ERROR_CODE) {
    switch (ERROR_CODE) {
      case INVALID_CREDENTIALS:
        let errorMessage =
          "That email or password doesn’t look right. Please try again or reset your password below. Too many failed attempts will lock your account.";
        return (
          <LoginForm
            missionName={missionName}
            errorMessage={errorMessage}
            loginChallenge={loginChallenge}
            email={email}
            isIovationEnabled={isIovationEnabledEnv}
          />
        );
      case WEBAUTHN_ASSERTION_FAILED:
        errorMessage = WEBAUTHN_ASSERTION_ERROR;
        return (
          <LoginForm
            missionName={missionName}
            errorMessage={errorMessage}
            hasResetPasswordLink={false}
            loginChallenge={loginChallenge}
            email={email}
            isIovationEnabled={isIovationEnabledEnv}
          />
        );
      case ACCOUNT_INACTIVE:
        return (
          <ActivateAccount
            errorMessage={errorMessage}
            email={email}
            missionName={missionName}
          />
        );
      case PASSWORD_EXPIRED:
        return <PasswordExpiredNotification />;
      case ACCOUNT_LOCKED.find((elem) => elem === ERROR_CODE):
        return <AccountLockedNotification missionName={missionName} />;
      case DENIED_IOVATION_RESPONSE:
        return (
          <Container soft size="xs">
            <AccountBlockedIovation />
          </Container>
        );
      case INVALID_CAPTCHA.find((elem) => elem === ERROR_CODE):
        return (
          <LoginForm
            missionName={missionName}
            hasResetPasswordLink={false}
            errorMessage="Unfortunately, there has been an error in the request. Please try again later"
            email={email}
            loginChallenge={loginChallenge}
            isIovationEnabled={isIovationEnabledEnv}
          />
        );
      default:
        return (
          <AccountBlockedNotification
            missionName={missionName}
            number={missionNumber}
          />
        );
    }
  } else {
    return (
      <LoginForm
        title={missionConfig.showTitle ? missionTitle : "Log in"}
        missionName={missionName}
        loginChallenge={loginChallenge}
        isIovationEnabled={isIovationEnabledEnv}
        infoMessage={
          showMfaSessionExpired ? mfaSessionExpiredLoginMessage : undefined
        }
      />
    );
  }
};

export default withGlobal((global) => ({
  missionName: global.readable_mission_name,
  missionTitle: global.title,
  missionNumber: global.call_centre_number,
}))(LoginPage);
